import React, { useState, useRef } from 'react';
import {
    Stack, Dialog, DialogContent, DialogTitle, Typography,
    IconButton, Box, Paper } from '@mui/material';
import { MapContainer, VideoOverlay } from 'react-leaflet';
import { BaseMap4326 } from '@map/base-map';
import { useLayers } from '@context/map-context';
import Draggable from "react-draggable";
import {
    CloseOutlined as CloseIcon, PlayCircle as PlayIcon,
    StopCircle as StopIcon } from '@mui/icons-material';
import {useSettings} from "@context";
import { CRS } from 'leaflet';

/**
 * AdcircAnimatedLayer
 * renders the layer animation
 *
 * @param layer
 * @returns React.ReactElement
 * @constructor
 */
export const AdcircAnimatedLayer = (layer) => {
    // this should come in the layer object
    const boundingBox = (layer.layer.boundingbox) ? JSON.parse(layer.layer.boundingbox) : [[29.423984, -84.956832], [24.264426, -76.898619]];

    // state to track play/stop of the vido loop
    const [play, setPlay] = useState(true);

    // reference to the animation control
    const ref = useRef(null);

    // toggles the layer visibility slider button
    const { toggleLayerVisibility } = useLayers();

    const { layerOpacity } = useSettings();

    // declare the width/height max of the dialog
    const maxWidth = 600;
    const maxHeight = 600;

    /**
     * find the center coordinates of a bounding box.
     *
     * Note: this presumes the data is:
     *   - an array of two points forming a bounding box
     *     - the first point is the top left corner of the bounding box
     *     - the second point is the lower right corner of the bounding box
     *
     *   since we are in the western part of the Northern Hemisphere (positive latitudes, negative longitudes),
     *   we take liberty with the data polarities for the calculation of the bounding box center.
     *
     */
    const findCenter = () => {
        // default the return value
        let ret_val = [30.0, -80.0];

        // if we got a value use it
        if (boundingBox) {
            // get the point between the latitude values
            const lat = (boundingBox[0][0] - boundingBox[1][0]) / 2 + boundingBox[1][0];

            // get the point between the longitude values
            const long = ((Math.abs(boundingBox[0][1]) - Math.abs(boundingBox[1][1])) / 2) + boundingBox[0][1];

            // save the center of the bounded box
            ret_val = [lat, long];
        }

        // return to the caller
        return ret_val;
    };

    /**
    * Close the dialog handler
    */
    const handleClose = () => {
        // toggle the slider button
        toggleLayerVisibility(layer.layer.id);

        // close the dialog
        // setOpen(false);
    };

    /**
     * Handles the play/stop button action
     */
    const handleVideoControl = (e) => {
        // if the play button was hit
        if (e.currentTarget.id === 'play') {
            // set the animation in motion
            setPlay(true);
        }
        // else if the stop button was hit
        else if (e.currentTarget.id === 'stop') {
            // stop the animation
            setPlay(false);
        }
    };

    // useEffect(() => {
    //     layer_opacity = LayerOpacity['ani'];
    // }, [layerOpacity['ani']]);

    return (
        <Dialog
            onClose={ handleClose }
            aria-labelledby="draggable-animation"
            open={ true }
            PaperComponent={ PaperComponent }
            disableEnforceFocus
            style={{ pointerEvents: 'none' }}
            PaperProps={{ sx: { pointerEvents: 'auto' } }}
            sx={{ ml: 0, '.MuiBackdrop-root': { backgroundColor: 'transparent' }}}
        >

            <DialogTitle
                id="draggable-animation"
                sx={{ p: 1, display: 'flex', alignItems: 'center', cursor: 'move', backgroundColor: 'lightblue' }}>
                <Typography
                    sx={{ mt: .25, wordWrap: 'break-word', width: maxWidth-35, flexWrap: "wrap", fontSize: 13}}>
                    { layer.layer.name }
                </Typography>
            </DialogTitle>

            <IconButton onClick={ handleClose } sx={{ position: 'absolute', right: 2, top: 0 }}>
                <CloseIcon sx={{ stroke: "currentColor", strokeWidth: 1.5 }}/>
            </IconButton>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ width: maxWidth, height: maxHeight }}>
                    <MapContainer center={ findCenter() } crs={CRS.EPSG4326} zoom={ 3 } style={{ height: "100%", width: "100%" }}>
                        <BaseMap4326 />

                        <Box sx={{ zIndex: 1000, m: 0, p: 0, border: 1, backgroundColor: "white", position: 'absolute', left: 50, top: 5 }}>
                            <Stack direction={{ xs: 'column' }} spacing={ 0 }>
                                <IconButton id="play" sx={{ m: 0, p: 0 }} onClick={ handleVideoControl }>
                                    <PlayIcon fontSize="large" color={"success"}/>
                                </IconButton>

                                <IconButton id="stop" sx={{ m: 0, p: 0 }} onClick={ handleVideoControl }>
                                    <StopIcon fontSize="large" color={"error"}/>
                                </IconButton>
                            </Stack>
                        </Box>

                        <VideoOverlay
                            ref={ref}
                            id="video-overlay"
                            url={ layer.layer.url }
                            autoplay={ true }
                            play={ play }
                            bounds={ boundingBox }
                            opacity={ layerOpacity['animate'].current }
                        />

                    </MapContainer>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

/**
* This creates a draggable area for the dialog content
*
* @param props
* @constructor
*/
function PaperComponent(props) {
    // create a reference to avoid the findDOMNode deprecation issue
    const nodeRef = useRef(null);

    // render the component
    return (
        <Draggable
            bounds="parent"
            nodeRef={ nodeRef }
            handle="#draggable-animation"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper ref={ nodeRef } { ...props } />
        </Draggable>
    );
}